import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Icon } from '@column/column-ui-kit';
import { DataTable, type DataTableColumnDef } from '@column/ui';

import { Loading, Section } from '~/components';
import { EventDetailModal } from '~/components/Dialog';
import { Empty as TableEmpty, getTimestampColumn, tableDefaults, useTimeFormat } from '~/components/Table';
import { EventRepository, parseEvent } from '~/repositories';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { EventEvent } from '~/typings/API';
import { formatString } from '~/util';

interface Params {
  id: string;
}

interface EventRow {
  id: string;
  eventType: string;
  createdAt: string;
  data: any;
}

const getCardAccountEventLabel = (eventType: string): string => {
  const parts = eventType.split('.');
  const finalPart = parts[parts.length - 1] || eventType;
  return formatString(finalPart);
};

const getColumns = (
  timeFormat: TableTimeFormat,
  onTimeFormatChange: (value: TableTimeFormat) => void
): DataTableColumnDef<EventRow>[] => [
  {
    header: 'Event',
    accessor: 'eventType',
    width: 'minmax(200px, 3fr)',
    cell: ({ value }) => <DataTable.TextCell value={getCardAccountEventLabel(value ?? '')} weight="medium" />,
  },
  getTimestampColumn<EventRow>({
    accessor: 'createdAt',
    label: 'Date',
    timeFormat,
    onTimeFormatChange,
    width: 'minmax(140px, 1fr)',
    fallback: <DataTable.TextCell value="-" />,
  }),
];

export const PageCardAccountEvents: React.FC = () => {
  const { id: cardAccountId } = useParams<keyof Params>() as Params;
  const { currentPlatform, isSandbox } = useSessionStore();
  const [isEventDetailModalOpen, setIsEventDetailModalOpen] = useState(false);
  const [selectedEventData, setSelectedEventData] = useState<any>(null);
  const { timeFormat, onTimeFormatChange } = useTimeFormat('card-account-events');

  const {
    data: eventsData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['card-account-events', cardAccountId, currentPlatform?.id, isSandbox],
    queryFn: () =>
      EventRepository.getAll({
        objectIds: [cardAccountId],
      } as any),
    enabled: !!cardAccountId,
  });

  const eventRows: EventRow[] = (eventsData?.events ?? []).map((event: EventEvent) => {
    const parsed = parseEvent(event);
    return {
      id: parsed.id,
      eventType: parsed.eventType,
      createdAt: parsed.createdAt,
      data: event.data,
    };
  });

  return (
    <>
      <EventDetailModal
        isOpen={isEventDetailModalOpen}
        onClose={() => setIsEventDetailModalOpen(false)}
        data={selectedEventData}
      />

      <Inner p={0}>
        <Section headline="Events">
          <Loading isLoading={isLoading}>
            <Inner px="var(--page-margin)" py={0}>
              <DataTable<EventRow>
                {...tableDefaults}
                columns={getColumns(timeFormat, onTimeFormatChange)}
                data={eventRows}
                rowKey={(row) => row.id}
                isLoading={isFetching}
                emptyContent={
                  <TableEmpty
                    icon={<Icon.AnimationSignal />}
                    title="No events found"
                    description="Events will appear here for this card account."
                  />
                }
                rowClassName="min-h-11 py-1"
                onRowClick={(row) => {
                  setSelectedEventData(row.data);
                  setIsEventDetailModalOpen(true);
                }}
              />
            </Inner>
          </Loading>
        </Section>
      </Inner>
    </>
  );
};
